import type { DocumentExportArtifactOptionsFragment } from "../client/generated";
import { AccessLoading } from "@acdc2/ui/components/access-loading";
import { Button } from "@acdc2/ui/components/button";
import { useAuth } from "react-oidc-context";
import { mdiChevronDown } from "@mdi/js";
import { Icon } from "@mdi/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDeleteArtifactMutation } from "../client/generated";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@acdc2/ui/components/dropdown-menu";

type Props = {
  fragment: DocumentExportArtifactOptionsFragment;
};

export function DocumentExportArtifactOptions({ fragment }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const auth = useAuth();

  const [deleteArtifact] = useDeleteArtifactMutation({
    update(cache) {
      cache.evict({ id: cache.identify(fragment) });
      cache.gc();
    },
  });

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteArtifact({
        variables: {
          artifactId: fragment.id,
        },
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDownload = async () => {
    if (!fragment.downloadUrl) return;

    const url = `${import.meta.env.VITE_ACDC2_BASE_API_URL}${fragment.downloadUrl}`;
    const headers = { Authorization: `Bearer ${auth.user?.access_token}` };
    const response = await fetch(url, { headers });

    const fileName = parseFilenameFromContentDisposition(response.headers);
    if (!fileName) return;

    const blob = await response.blob();

    const element = document.createElement("a");
    element.setAttribute("href", URL.createObjectURL(blob));
    element.setAttribute("download", fileName);
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline">
          <span>{t("DocumentExportArtifactOptions.label")}</span>
          <Icon size="1.25rem" path={mdiChevronDown} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuItem className="justify-between" onSelect={handleDelete}>
          <span>{t("DocumentExportArtifactOptions.items.delete")}</span>
          {isDeleting && <AccessLoading className="h-4" />}
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={!fragment.downloadUrl}
          onSelect={handleDownload}
        >
          <span>{t("DocumentExportArtifactOptions.items.download")}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function parseFilenameFromContentDisposition(headers: Headers): string | null {
  const cd = headers.get("Content-Disposition");
  if (!cd) return null;

  // RFC 5987 filename* support
  const filenameStarMatch = cd.match(/filename\*=UTF-8''([^;\n]+)/i);
  if (filenameStarMatch && filenameStarMatch[1]) {
    try {
      return decodeURIComponent(filenameStarMatch[1]);
    } catch {
      // fall through to other strategies
    }
  }

  // Quoted filename
  const quotedMatch = cd.match(/filename="([^"]+)"/i);
  if (quotedMatch && quotedMatch[1]) {
    return quotedMatch[1];
  }

  // Unquoted filename
  const unquotedMatch = cd.match(/filename=([^;\n]+)/i);
  if (unquotedMatch && unquotedMatch[1]) {
    return unquotedMatch[1].trim();
  }

  return null;
}
